import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useTranslation } from "../../../i18n/I18nContext";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { listBlogPosts } from "../../../services/platformBlogService";
import { LANDING_MAX, LANDING_PAD_X } from "./landingLayout";

export function LandingInsights() {
  const { t, lang } = useTranslation();
  const lp = useLocalePath();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { posts: list } = await listBlogPosts({ lang, page: 1, limit: 3 });
        if (!cancelled) setPosts(list || []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Always render the section; show skeletons while loading or when no posts yet
  const showSkeleton = loading || !posts.length;

  return (
    <section id="insights" aria-labelledby="insights-title" className="w-full py-12 sm:py-14 lg:py-[76px]">
      <div className={`${LANDING_MAX} ${LANDING_PAD_X}`}>
        <div className="mb-8 flex flex-col gap-4 text-center sm:mb-10 sm:gap-5 lg:mb-[50px]">
          <h2
            id="insights-title"
            className="font-bold leading-[1.3] text-[28px] sm:text-[34px] lg:text-5xl xl:text-[48px]"
          >
            {t("insights.title")}
          </h2>
          <p className="text-base text-[#4B5563] sm:text-lg lg:text-[20px]">{t("insights.subtext")}</p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {showSkeleton &&
            [0, 1, 2].map((n) => (
              <div
                key={`sk-${n}`}
                className="flex flex-col overflow-hidden rounded-[24px] border border-white/60 bg-white shadow-figma-card"
              >
                <div className="h-[200px] w-full animate-pulse bg-gradient-to-br from-frosted-blue to-soft-ice-blue" />
                <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
                  <div className="h-5 w-3/4 animate-pulse rounded bg-frosted-blue" />
                  <div className="h-3 w-full animate-pulse rounded bg-soft-ice-blue" />
                  <div className="h-3 w-5/6 animate-pulse rounded bg-soft-ice-blue" />
                </div>
              </div>
            ))}
          {!showSkeleton && posts.map((post) => {
            const catSlug = post.category?.slug || "general";
            const href = lp(`/blog/${catSlug}/${post.slug}`);
            const img = post.featured_image_url || post.cover_image_url;
            return (
              <Link
                key={post.id || post.slug}
                to={href}
                className="group flex flex-col overflow-hidden rounded-[24px] border border-white/60 bg-white shadow-figma-card transition-all hover:shadow-[0_8px_32px_rgba(58,78,139,0.22)]"
              >
                <div className="relative h-[200px] w-full overflow-hidden bg-gradient-to-br from-[#E3EBFF] to-[#F1F5FF]">
                  {img ? (
                    <img
                      src={img}
                      alt={post.title || "PromBiz blog post cover"}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Icon icon="mdi:newspaper-variant-outline" className="h-14 w-14 text-primary/40" />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="line-clamp-2 font-semibold text-[#0F172A] text-lg leading-snug group-hover:text-primary">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-2 line-clamp-3 flex-1 text-[#6B7280] text-sm leading-relaxed">{post.excerpt}</p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1 font-medium text-primary text-sm">
                    {t("insights.readMore")}
                    <Icon icon="mdi:arrow-right" className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-8 flex justify-center sm:mt-10">
          <Link
            to={lp("/blog")}
            className="inline-flex items-center gap-2 rounded-[15px] border border-primary px-6 py-3 font-semibold text-primary text-sm transition-colors hover:bg-primary hover:text-white"
          >
            {t("insights.viewBlog")}
            <Icon icon="mdi:arrow-right" className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
